import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env';
import { DailyStatistic, MonthlyStatistic, PeriodFilter, SmsTypeStatistic } from '@models/statistics/statistics.model';
import { ListSubaccountStatistics, SubaccountStatistics } from '@models/subaccount/statistics/subaccount-statistics';
import { ApiGetSubaccounts } from '@models/subaccount/subaccount';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SubaccountStatisticsService {
  private readonly baseUrl;

  constructor(private http: HttpClient, private translateService: TranslateService) {
    this.baseUrl = environment.serverBaseUrl;
  }

  getMonthlySmsSubStatistics(idSubaccountUser, filter: PeriodFilter, fillGaps: boolean): Observable<MonthlyStatistic[]> {
    const headers = new HttpHeaders().set('Content-type', 'application/json').set('Accept', 'application/json');
    const body = this.mapToSubaccountFilterDateStatisticsDTO(idSubaccountUser, filter);

    const fromDate = moment(filter.dateFrom);
    const toDate = moment(filter.dateTo);

    let translatedMonths = [];

    let observable = this.translateService.get(Array.from({ length: 12 }, (_, i) => `month_${i + 1}`)).pipe(
      tap(months => (translatedMonths = months)),
      switchMap(() => this.http.post<MonthlyStatistic[]>(this.baseUrl + 'subaccount/statistics/month', body, { headers }))
    );

    if (fillGaps) {
      observable = observable.pipe(
        map(stats => {
          const monthsMap = stats.reduce((acc, stat) => {
            acc[stat.month] = stat;
            return acc;
          }, {});

          const filledStats = [];

          for (const m = fromDate; m.isBefore(toDate); m.add(1, 'months')) {
            const month = m.format('YYYY-MM');
            const translatedMonth = translatedMonths[`month_${m.month() + 1}`];
            const label = `${translatedMonth} ${m.format('YYYY')}`;

            const stat = monthsMap[month] || { month, label, count: 0 };
            filledStats.push(stat);
          }

          return filledStats;
        })
      );
    }

    return observable;
  }

  private mapToSubaccountFilterDateStatisticsDTO(idSubaccountUser, filters: PeriodFilter) {
    const idSub = idSubaccountUser ? idSubaccountUser : null;
    const filterDate = {
      dateFrom: this.formatDate(filters.dateFrom),
      dateTo: this.formatDate(filters.dateTo)
    };
    if (idSubaccountUser) {
      return { idSubaccountUser: idSub, filterDate };
    }
    return { filterDate };
  }

  private formatDate(date: Date) {
    const mDate = moment(date);
    const timezone = mDate.format('Z').replace(':', '');
    return mDate.format(`YYYY-MM-DDThh:mm:ss`) + timezone;
  }

  getDailySmsSubStatistics(idSubaccountUser, filter: PeriodFilter, fillGaps: boolean): Observable<DailyStatistic[]> {
    const headers = new HttpHeaders().set('Content-type', 'application/json').set('Accept', 'application/json');
    const body = this.mapToSubaccountFilterDateStatisticsDTO(idSubaccountUser, filter);

    const fromDate = moment(filter.dateFrom);
    const toDate = moment(filter.dateTo);

    let observable = this.http.post<DailyStatistic[]>(this.baseUrl + 'subaccount/statistics/day', body, { headers });

    if (fillGaps) {
      observable = observable.pipe(
        map(stats => {
          const daysMap = stats.reduce((acc, stat) => {
            acc[stat.day] = stat;
            return acc;
          }, {});

          const filledStats = [];

          for (const m = fromDate; m.isBefore(toDate); m.add(1, 'days')) {
            const day = m.format('YYYY-MM-DD');
            const label = m.format('DD/MM/YYYY');

            const stat = daysMap[day] || { day, label, count: 0 };
            filledStats.push(stat);
          }

          return filledStats;
        })
      );
    }

    return observable;
  }

  getTypeSmsSubStatistics(idSubaccountUser, filter: PeriodFilter): Observable<SmsTypeStatistic[]> {
    const headers = new HttpHeaders().set('Content-type', 'application/json').set('Accept', 'application/json');
    const body = this.mapToSubaccountFilterDateStatisticsDTO(idSubaccountUser, filter);
    return this.http.post<SmsTypeStatistic[]>(this.baseUrl + 'subaccount/statistics/message-type', body, { headers });
  }

  getListSubaccountStatistics(
    pageNum?,
    pageSize?,
    sortColumn?: string,
    sortDirection?: string,
    month?: string,
    year?: string,
    toMonth?: string,
    toYear?: string,
    body?: ApiGetSubaccounts
  ): Observable<ListSubaccountStatistics> {
    let params = new HttpParams();
    if (pageNum) {
      params = params.append('page', pageNum);
    } else {
      params = params.append('page', '1');
    }
    if (pageSize) {
      params = params.append('size', pageSize);
    } else {
      params = params.append('size', '10');
    }
    if (sortColumn) {
      params = params.append('sortColumn', sortColumn);
    } else {
      params = params.append('sortColumn', 'LOGIN');
    }
    if (sortDirection) {
      params = params.append('sortDirection', sortDirection);
    } else {
      params = params.append('sortDirection', 'a');
    }
    if (month) {
      params = params.append('month', month);
    }

    if (year) {
      params = params.append('year', year);
    }
    if (toMonth) {
      params = params.append('toMonth', toMonth);
    }

    if (toYear) {
      params = params.append('toYear', toYear);
    }

    const url = 'subaccount/statistics/lists';
    return this.http
      .post(this.baseUrl + url, body, {
        params
      })
      .pipe(
        map(data => {
          return ListSubaccountStatistics.deserialize({
            data: (data && (data as any).data ? ((data as any).data as Array<string>) : []).map(data2 =>
              SubaccountStatistics.deserialize(data2)
            ),
            total: (data as any).total
          });
        })
      );
  }
}
